//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React, { useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RadioGroup,
  RadioOption,
} from 'unison';

import { useBoundActionCreators } from '@hooks/useCuralateStore';
import Cuid from '@models/Cuid';
import { IShareRejectionReason } from '@share/models/shareStateModel';
import { actions as streamDrawerActions } from '@streams/actions/streamDrawerActions';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

interface Props {
  rejectionReasons: IShareRejectionReason[];
  rejectItem: (cuid: Cuid, rejectionCode: number) => Promise<void>;
}

export const StreamsDrawerRejectionReasonsModal = ({
  rejectionReasons,
  rejectItem,
}: Props) => {
  const [selectedRejectionCode, setSelectedRejectionCode] =
    useState<string>(null);
  const { closeRejectionReasonsModal } =
    useBoundActionCreators<typeof streamDrawerActions>(streamDrawerActions);
  const { isRejectionReasonsModalOpen, rejectionReasonModalCuid } = useSelector(
    (state) => state.streams.streamsDrawer,
  );

  const closeAndResetModal = () => {
    closeRejectionReasonsModal();
    setSelectedRejectionCode(null);
  };

  const onSave = async () => {
    rejectItem(rejectionReasonModalCuid, parseInt(selectedRejectionCode, 10));
    closeAndResetModal();
  };

  return (
    <Modal isOpen={isRejectionReasonsModalOpen}>
      <ModalHeader onCancel={closeAndResetModal}>Reject Item</ModalHeader>
      <RejectionReasonsModalBody>
        <RejectionReasonsModalBodyContent>
          Select the reason for rejecting this item below. We'll use your
          feedback to improve the quality of content we curate for you in the
          future.
          <RejectionReasonRadioGroup
            handleChange={setSelectedRejectionCode}
            value={selectedRejectionCode}
          >
            {rejectionReasons?.map((reason, idx) => (
              <RadioOption key={idx} value={reason.rejectionCode.toString()}>
                {reason.rejectionDescription}
              </RadioOption>
            ))}
          </RejectionReasonRadioGroup>
        </RejectionReasonsModalBodyContent>
      </RejectionReasonsModalBody>
      <ModalFooter>
        <CancelButton onClick={closeAndResetModal}>Cancel</CancelButton>
        <SaveButton
          isDisabled={selectedRejectionCode === null}
          onClick={onSave}
        >
          Save
        </SaveButton>
      </ModalFooter>
    </Modal>
  );
};

const RejectionReasonRadioGroup = styled(RadioGroup)(
  ({ theme }) => `
  margin: ${theme.spacings[4]} 0;
`,
);

const RejectionReasonsModalBody = styled(ModalBody).attrs({
  className: 'cu-d-block cu-wide-measure',
})`
  padding-bottom: 0 !important;
`;

const RejectionReasonsModalBodyContent = styled.div(
  ({ theme }) => `
  height: ${theme.smallMeasure};
  overflow-y: scroll;
`,
);

const CancelButton = styled(Button).attrs({
  className: 'cu-button-secondary',
})``;

const SaveButton = styled(Button).attrs({
  className: 'cu-button-primary',
})(
  ({ theme }) => `
  margin-left: ${theme.spacings[2]};
`,
);

export default StreamsDrawerRejectionReasonsModal;
